// Fills the DG Summary's UNIT-BY-UNIT BREAKDOWN table from the survey.
//
// The block sits under a "UNIT-BY-UNIT BREAKDOWN" header: a column-header row
// (Unit | Tons | Yrs | Action | Model # | Serial # | Cost) followed by one
// row per unit and an Add Tonnage row. Cost is a live formula (left alone);
// the data columns are filled per unit from the survey answers. Columns are
// located by their header text, rows by their "Unit N" label, so template
// layout shifts don't misfire. Units absent from the survey stay blank.

import type { CellWrite } from "./xlsxTemplatePatcher.ts";
import { findSheetName, readSheetCells } from "./xlsxTemplatePatcher.ts";
import { SurveyAnswers } from "./gcEvaluationFill.ts";
import { SUMMARY_SHEET_RE } from "./exportCore.ts";

const SUMMARY_SHEET = "DG Leasing - Summary";

const HEADER_COLUMNS: Record<string, string> = {
  tons: "tons",
  yrs: "yrs",
  action: "action",
  "model #": "model",
  "serial #": "serial",
};

/** Pure core: locate the breakdown block and emit per-unit writes. */
export function buildUnitBreakdownWrites(
  cells: Map<string, string>,
  answers: SurveyAnswers,
  sheetName: string = SUMMARY_SHEET
): CellWrite[] {
  let headerRow: number | null = null;
  for (const [ref, text] of cells) {
    const m = ref.match(/^B(\d+)$/);
    if (m && text.trim().toUpperCase() === "UNIT-BY-UNIT BREAKDOWN") {
      headerRow = Number(m[1]) + 1; // column headers on the next row
      break;
    }
  }
  if (headerRow === null) return [];

  // Header text → column letter (Tons/Yrs/Action/Model #/Serial #).
  const colByField = new Map<string, string>();
  for (const [ref, text] of cells) {
    const m = ref.match(/^([A-Z]+)(\d+)$/);
    if (!m || Number(m[2]) !== headerRow) continue;
    const field = HEADER_COLUMNS[text.trim().toLowerCase()];
    if (field) colByField.set(field, m[1]);
  }
  if (colByField.size === 0) return [];

  const writes: CellWrite[] = [];
  const push = (field: string, row: number, value: string | number | null) => {
    const col = colByField.get(field);
    if (col && value !== null && value !== "") {
      writes.push({ sheet: sheetName, cell: `${col}${row}`, value });
    }
  };

  for (const [ref, text] of cells) {
    const m = ref.match(/^B(\d+)$/);
    if (!m) continue;
    const row = Number(m[1]);
    if (row <= headerRow || row > headerRow + 12) continue;
    const label = text.trim();

    const unit = label.match(/^Unit (\d+)$/);
    if (unit) {
      const n = unit[1];
      push("tons", row, answers.num(`Unit ${n} — Tonnage`));
      push("yrs", row, answers.num(`Unit ${n} — Age (years)`) ?? answers.num(`Unit ${n} — Age`));
      push("action", row, answers.text(`Unit ${n} — Action`));
      push("model", row, answers.text(`Unit ${n} — Model Number`));
      push("serial", row, answers.text(`Unit ${n} — Serial Number`));
      continue;
    }

    if (/^Add Tonnage/i.test(label)) {
      const deficit = answers.num("Tonnage deficit / add");
      const zone = answers.text("Climate Zone") ?? answers.text("Zone");
      if (deficit !== null && deficit > 0) {
        writes.push({
          sheet: sheetName,
          cell: `B${row}`,
          value: `Add ${deficit}T${zone ? ` (Zone ${zone} deficit)` : " (Zone deficit)"}`,
        });
        push("model", row, `${deficit}T × $3,500/ton install`);
      }
    }
  }
  return writes;
}

/** Breakdown writes for a template workbook; [] when it has no Summary sheet. */
export async function collectUnitBreakdownWrites(
  templateBuf: ArrayBuffer,
  answers: SurveyAnswers
): Promise<CellWrite[]> {
  const summarySheet = (await findSheetName(templateBuf, SUMMARY_SHEET_RE)) ?? SUMMARY_SHEET;
  const cells = await readSheetCells(templateBuf, summarySheet);
  if (cells.size === 0) return [];
  return buildUnitBreakdownWrites(cells, answers, summarySheet);
}
